/** 充值活动总前端开关：false 时关闭全站前端入口与弹窗 */
export const ENABLE_DEPOSIT_BONUS_CAMPAIGN = true;

/** 充值赠送档：与活动中心阶梯一致——按最低触发金额从高到低取最高命中档。 */

export const DEPOSIT_BONUS_TIERS = Object.freeze([
  Object.freeze({ usd: 10, bonusUsd: 1, percent: 10, tone: 'red' }),
  Object.freeze({ usd: 30, bonusUsd: 4, percent: 13, tone: 'red' }),
  /** featured：运营指定「最高赠送比例」角标档 */
  Object.freeze({ usd: 50, bonusUsd: 7, percent: 14, tone: 'gold', featured: true }),
  Object.freeze({ usd: 100, bonusUsd: 12, percent: 12, tone: 'red' }),
]);

/** 顶栏上下滚动主推：总宣 + 四档阶梯 */
export const DEPOSIT_BONUS_PROMO_SLIDES = Object.freeze([
  Object.freeze({ maxUsd: 12, tone: 'gold', key: 'bonusPromoLineHeadline' }),
  Object.freeze({ usd: 10, bonusUsd: 1, tone: 'red', key: 'bonusPromoLineTier' }),
  Object.freeze({ usd: 30, bonusUsd: 4, tone: 'red', key: 'bonusPromoLineTier' }),
  Object.freeze({ usd: 50, bonusUsd: 7, tone: 'gold', key: 'bonusPromoLineTier' }),
  Object.freeze({ usd: 100, bonusUsd: 12, tone: 'red', key: 'bonusPromoLineTier' }),
]);

const TIER_MATCH_EPS = 0.009;

/** 「最高赠送比例」角标档：优先 featured，否则取 percent 最大 */
export function resolveBestDepositBonusTier() {
  const featured = DEPOSIT_BONUS_TIERS.find((tier) => tier.featured);
  if (featured) return featured;
  let best = DEPOSIT_BONUS_TIERS[0];
  for (const tier of DEPOSIT_BONUS_TIERS) {
    if (tier.percent > best.percent) best = tier;
  }
  return best;
}

/**
 * 入账 USD 命中的赠送档（高→低，取最高门槛；对齐活动中心阶梯）。
 * @param {number|string} usd
 * @returns {{ usd: number, bonusUsd: number, percent: number, tone?: string } | null}
 */
export function resolveDepositBonus(usd, participationData = null) {
  const n = Number(usd);
  if (!Number.isFinite(n) || n <= 0) return null;
  let tiers = DEPOSIT_BONUS_TIERS;
  let isHighestTierOnly = false;
  let isSealed = false;

  if (
    participationData &&
    Array.isArray(participationData.tiers) &&
    participationData.tiers.length > 0
  ) {
    isHighestTierOnly = participationData.tierClaimMode === 'HIGHEST_TIER_ONLY';
    isSealed = Boolean(participationData.sealed);
    if (isSealed || (isHighestTierOnly && participationData.participated)) {
      return null;
    }
    tiers = participationData.tiers
      .filter((t) => !t.claimed && t.claimable !== false)
      .map((t) => ({
        usd: Number(t.minDeposit) || 0,
        bonusUsd: Number(t.reward) || 0,
      }));
  }

  const sorted = [...tiers].sort((a, b) => b.usd - a.usd);
  return sorted.find((tier) => n + TIER_MATCH_EPS >= tier.usd) ?? null;
}

/**
 * 快捷金额按钮高亮：仅精确档（默认 $10 时高亮「充10送1」；$20 等非档位不高亮）。
 * @param {number|string} usd
 */
export function matchDepositBonusTiers(usd) {
  const n = Number(usd);
  if (!Number.isFinite(n) || n <= 0) return null;
  return DEPOSIT_BONUS_TIERS.find((tier) => Math.abs(n - tier.usd) < 0.51) ?? null;
}

/**
 * 充值弹窗进度：仅按历史真实入账 USD（订单 CREDITED / 条件回退），不含本笔输入。
 * @param {number|string} usd
 * @returns {{
 *   currentUsd: number,
 *   hitTier: (typeof DEPOSIT_BONUS_TIERS)[number] | null,
 *   nextTier: (typeof DEPOSIT_BONUS_TIERS)[number] | null,
 *   remainingUsd: number,
 *   progressPercent: number,
 *   maxReached: boolean,
 *   tiers: Array<(typeof DEPOSIT_BONUS_TIERS)[number] & { status: 'done' | 'next' | 'locked' }>
 * }}
 */
export function resolveDepositBonusProgress(usd, participationData = null) {
  const currentUsd = Math.max(0, Number(usd) || 0);

  let baseTiers = DEPOSIT_BONUS_TIERS;
  let isHighestTierOnly = false;
  let isSealed = false;

  if (
    participationData &&
    Array.isArray(participationData.tiers) &&
    participationData.tiers.length > 0
  ) {
    isHighestTierOnly = participationData.tierClaimMode === 'HIGHEST_TIER_ONLY';
    isSealed = Boolean(participationData.sealed);
    baseTiers = participationData.tiers.map((t, idx) => ({
      usd: Number(t.minDeposit) || 0,
      bonusUsd: Number(t.reward) || 0,
      percent: Number(t.minDeposit)
        ? Math.round((Number(t.reward) / Number(t.minDeposit)) * 100)
        : 0,
      tone: idx === 0 ? 'gold' : 'red',
      featured: idx === 0,
      claimed: Boolean(t.claimed),
      claimable: t.claimable !== false,
      claimedAmount: Number(t.claimedAmount) || 0,
    }));
  }

  const ascending = [...baseTiers].sort((a, b) => a.usd - b.usd);
  const hitTier = resolveDepositBonus(currentUsd);

  let nextTier = null;
  if (!isSealed) {
    nextTier =
      ascending.find((tier) => {
        if (tier.claimed) return false;
        return currentUsd + TIER_MATCH_EPS < tier.usd;
      }) ?? null;
  }

  const maxReached = isSealed || !nextTier;
  const remainingUsd = nextTier ? Math.max(0, Number((nextTier.usd - currentUsd).toFixed(2))) : 0;
  const progressPercent = maxReached
    ? 100
    : Math.max(0, Math.min(100, Math.round((currentUsd / (nextTier ? nextTier.usd : 1)) * 100)));

  let sawNext = false;
  const tiers = ascending.map((tier) => {
    if (
      tier.claimed ||
      (isHighestTierOnly && participationData && participationData.participated) ||
      currentUsd + TIER_MATCH_EPS >= tier.usd
    ) {
      return { ...tier, status: 'done' };
    }
    if (!sawNext && tier.claimable !== false) {
      sawNext = true;
      return { ...tier, status: 'next' };
    }
    return { ...tier, status: 'locked' };
  });

  return {
    currentUsd,
    hitTier,
    nextTier,
    remainingUsd,
    progressPercent,
    maxReached,
    tiers,
    isHighestTierOnly,
    isSealed,
  };
}

export function resolveDepositBonusLadderCapUsd() {
  let max = 0;
  for (const tier of DEPOSIT_BONUS_TIERS) {
    if (tier.usd > max) max = tier.usd;
  }
  return max;
}

/**
 * 是否已领完阶梯：历史最高入账（或本笔）已达最高门槛。
 * @param {number|string} maxCreditedUsd
 */
export function hasCompletedDepositBonusLadder(maxCreditedUsd) {
  const n = Number(maxCreditedUsd);
  if (!Number.isFinite(n) || n <= 0) return false;
  return n + TIER_MATCH_EPS >= resolveDepositBonusLadderCapUsd();
}
